import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { retry } from 'rxjs';
import { WebSocketService } from '@shared/data-access/websocket.service';

interface LiveAlert {
	type: string;
	message: string;
	data: {
		serviceId?: number;
		[key: string]: unknown;
	};
	timestamp: string;
}

/** Reintento mientras la conexión WebSocket (la abre la barra superior) aún no existe */
const LISTEN_RETRY_MS = 3000;

/**
 * Avisos en vivo para el admin que llegan por WebSocket y no se guardan como notificación:
 * desvíos de la ruta planificada (route-alert) y entradas/salidas de geocercas (geofence-alert).
 */
@Component({
	selector: 'app-live-alerts',
	standalone: true,
	imports: [ToastModule, ButtonModule],
	providers: [MessageService],
	template: `
		<p-toast key="live-alerts" position="bottom-right" [life]="10000">
			<ng-template let-message pTemplate="message">
				<div class="d-flex flex-column gap-2 w-100">
					<strong>{{ message.summary }}</strong>
					<span>{{ message.detail }}</span>
					@if (message.data?.serviceId) {
						<div>
							<p-button
								label="Ver servicio"
								icon="pi pi-external-link"
								styleClass="p-button-sm p-button-outlined"
								(onClick)="openService(message.data.serviceId)"
							></p-button>
						</div>
					}
				</div>
			</ng-template>
		</p-toast>
	`
})
export class LiveAlertsComponent implements OnInit {
	private readonly webSocketService = inject(WebSocketService);
	private readonly messageService = inject(MessageService);
	private readonly router = inject(Router);
	private readonly destroyRef = inject(DestroyRef);

	ngOnInit(): void {
		this.listen('route-alert', (alert) => ({
			severity: alert.type === 'route_deviation' ? 'error' : 'success',
			summary: alert.type === 'route_deviation' ? 'Desvío de ruta' : 'Regreso a la ruta',
			sticky: alert.type === 'route_deviation'
		}));
		this.listen('geofence-alert', (alert) => ({
			severity: 'info',
			summary: alert.type === 'geofence_enter' ? 'Entrada a geocerca' : 'Salida de geocerca',
			sticky: false
		}));
	}

	openService(serviceId: number): void {
		this.router.navigate(['/services/list/detail', serviceId]);
	}

	private listen(
		event: string,
		toToast: (alert: LiveAlert) => { severity: string; summary: string; sticky: boolean }
	): void {
		this.webSocketService
			.listen<LiveAlert>(event)
			.pipe(retry({ delay: LISTEN_RETRY_MS }), takeUntilDestroyed(this.destroyRef))
			.subscribe((alert) => {
				const toast = toToast(alert);
				this.messageService.add({
					key: 'live-alerts',
					severity: toast.severity,
					summary: toast.summary,
					detail: alert.message,
					sticky: toast.sticky,
					data: alert.data
				});
			});
	}
}
